import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

const translations = {
  fr: {
    // Navigation
    home: 'Accueil',
    login: 'Connexion',
    register: "S'inscrire",
    logout: 'Déconnexion',
    dashboard: 'Tableau de bord',
    
    // Landing Page
    heroTitle: 'Reprenez votre souffle',
    heroSubtitle: 'Évaluez votre dépendance au tabac et commencez votre parcours vers une vie sans fumée.',
    startNow: 'Commencer maintenant',
    alreadyMember: 'Déjà membre ?',
    
    // Auth
    firstName: 'Prénom',
    lastName: 'Nom',
    email: 'Email',
    password: 'Mot de passe',
    dateOfBirth: 'Date de naissance',
    createAccount: 'Créer un compte',
    loginToAccount: 'Se connecter',
    noAccount: "Pas encore de compte ?",
    hasAccount: 'Déjà un compte ?',
    registerHere: "S'inscrire ici",
    loginHere: 'Se connecter ici',
    
    // Dashboard
    welcome: 'Bienvenue',
    takeTest: 'Passer le test Fagerström',
    testDescription: 'Évaluez votre niveau de dépendance au tabac en répondant à 6 questions simples.',
    yourScore: 'Votre score',
    dependencyLevel: 'Niveau de dépendance',
    testHistory: 'Historique des tests',
    noTestYet: "Vous n'avez pas encore passé de test.",
    lastTest: 'Dernier test',
    
    // Questionnaire
    question: 'Question',
    of: 'sur',
    next: 'Suivant',
    previous: 'Précédent',
    submit: 'Soumettre',
    
    // Results
    yourResult: 'Votre résultat',
    scoreInterpretation: 'Interprétation du score',
    veryLow: 'Dépendance très faible',
    low: 'Dépendance faible',
    medium: 'Dépendance moyenne',
    high: 'Dépendance forte',
    veryHigh: 'Dépendance très forte',
    backToDashboard: 'Retour au tableau de bord',
    retakeTest: 'Repasser le test',
    
    // Messages
    veryLowMessage: 'Félicitations ! Votre dépendance au tabac est très faible. C\'est le moment idéal pour arrêter.',
    lowMessage: 'Votre dépendance est faible. Vous avez de bonnes chances de réussir à arrêter.',
    mediumMessage: 'Votre dépendance est modérée. Un soutien pourrait vous aider à arrêter.',
    highMessage: 'Votre dépendance est élevée. Consultez un professionnel pour vous accompagner.',
    veryHighMessage: 'Votre dépendance est très élevée. Un accompagnement médical est recommandé.',
    
    // Errors
    errorOccurred: 'Une erreur est survenue',
    invalidCredentials: 'Email ou mot de passe incorrect',
    emailExists: 'Cet email est déjà utilisé',
  },
  en: {
    // Navigation
    home: 'Home',
    login: 'Login',
    register: 'Register',
    logout: 'Logout',
    dashboard: 'Dashboard',
    
    // Landing Page
    heroTitle: 'Take a fresh breath',
    heroSubtitle: 'Assess your tobacco dependence and start your journey to a smoke-free life.',
    startNow: 'Start now',
    alreadyMember: 'Already a member?',
    
    // Auth
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    password: 'Password',
    dateOfBirth: 'Date of birth',
    createAccount: 'Create account',
    loginToAccount: 'Log in',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    registerHere: 'Register here',
    loginHere: 'Login here',
    
    // Dashboard
    welcome: 'Welcome',
    takeTest: 'Take the Fagerström test',
    testDescription: 'Assess your tobacco dependence level by answering 6 simple questions.',
    yourScore: 'Your score',
    dependencyLevel: 'Dependency level',
    testHistory: 'Test history',
    noTestYet: "You haven't taken a test yet.",
    lastTest: 'Last test',
    
    // Questionnaire
    question: 'Question',
    of: 'of',
    next: 'Next',
    previous: 'Previous',
    submit: 'Submit',
    
    // Results
    yourResult: 'Your result',
    scoreInterpretation: 'Score interpretation',
    veryLow: 'Very low dependence',
    low: 'Low dependence',
    medium: 'Medium dependence',
    high: 'High dependence',
    veryHigh: 'Very high dependence',
    backToDashboard: 'Back to dashboard',
    retakeTest: 'Retake test',
    
    // Messages
    veryLowMessage: 'Congratulations! Your tobacco dependence is very low. This is the perfect time to quit.',
    lowMessage: 'Your dependence is low. You have good chances of successfully quitting.',
    mediumMessage: 'Your dependence is moderate. Support could help you quit.',
    highMessage: 'Your dependence is high. Consider consulting a professional for support.',
    veryHighMessage: 'Your dependence is very high. Medical support is recommended.',
    
    // Errors
    errorOccurred: 'An error occurred',
    invalidCredentials: 'Invalid email or password',
    emailExists: 'This email is already registered',
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'fr';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    return translations[language][key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
